import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, first, map, mergeMap } from 'rxjs/operators';
import { CookieAdapterService } from '../../core/cookie-adapter.service';
import { OeAppService } from '../oe-app.service';
import { RequireAdapterService } from '../require-adapter.service';
import { ECTokensDto } from '../types/ECTokensDto';
import { StudyBundleCourseDto } from '../types/StudyBundleCourseDto';
import { ECModalComponent } from './ec-modal.component';
import { StudyBundleCourseStatusType } from '../types/StudyBundleCourseStatusType';
import { ImmersionCourseService } from '../immersion-course.service';
import { OeAppPersonService } from '../oe-app-person.service';
import { getECLanguageToUse } from './ec-supported-languages';
import { CourseContentType } from '../types/course-content-type';

declare var window: any;

@Injectable()
export class ECVideoService {
  onClose = new Subject();
  readonly _loading = new BehaviorSubject(false);
  readonly loading: Observable<boolean> = this._loading.asObservable();

  private maxRetries = 5;
  private startTime = 0;
  private tokens: ECTokensDto;
  private commonConst;
  private video: StudyBundleCourseDto;
  private player;
  private modalRef: NgbModalRef;
  private videoLanguage = 'en';

  constructor(
    private http: HttpClient,
    private cookieService: CookieAdapterService,
    private oeApp: OeAppService,
    private modalService: NgbModal,
    private requireService: RequireAdapterService,
    private immersionCourseService: ImmersionCourseService,
    private oeAppPersonService: OeAppPersonService
  ) {
    this.videoLanguage = getECLanguageToUse(oeAppPersonService.preferredLang);
  }

  initialize(callback, onError?) {
    const url = this.oeApp.getData('common').englishCentralSDKUrl;
    var sdkLoaded = document.querySelector('script[src="' + url + '"]');

    if (sdkLoaded) {
      return;
    }
    const script = document.createElement('script');
    const that = this;
    script.src = url;
    script.async = true;
    document.body.appendChild(script);
    script.onload = () => {
      window.ECSDK.setLoginErrorHandler(function () {
        console.error('Error Loading English Central');
        that.getEcTokenAndSetCookie().subscribe(() => {
          that.playVideo();
        });
      });

      window.ECSDK.setOnReadyHandler(function () {
        that.setVideoLoading(false);
      });
      callback();
    };
    script.onerror = () => {
      console.error('Could not load EC');
      if (onError instanceof Function) {
        onError();
      }
    };

    window.MobileEC = {};

    this.commonConst = this.requireService.loadCommonConstants();
  }

  private setVideoLoading(value: boolean) {
    if (!this.modalRef || !this.modalRef.componentInstance) {
      return;
    }
    this.modalRef.componentInstance.loading = value;
  }

  open(video: StudyBundleCourseDto) {
    this.video = video;
    if (this.oeApp.isMobileDevice()) {
      this.playVideoMobile();
    } else {
      this.modalRef = this.modalService.open(ECModalComponent, {
        windowClass: 'ec-video-modal',
        backdrop: 'static',
      });
      this.player = this.modalRef.componentInstance.player.nativeElement;
      this.modalRef.componentInstance.loaded.pipe(first()).subscribe(() => {
        this.playVideo();
      });
      this.modalRef.componentInstance.title = video.title;
      this.modalRef.result.then(() => {
        this.close();
      });
    }
  }

  close() {
    this._loading.next(true);
    if (this.modalRef) {
      this.modalRef.close();
    }
    // TEMP FIX: Ignore video report if video is complete
    const isAlreadyComplete = this.video.status === StudyBundleCourseStatusType.COMPLETE;

    const reportProgress = () => (isAlreadyComplete ? this.saveVideoProgress() : this.reportEcVideoProgress());

    reportProgress().subscribe(() => {
      window.ECSDK.closeWidget();
      this._loading.next(false);
      this.onClose.next(this.video.id);
      this.modalRef = null;
      this.video = null;
      this.startTime = 0;
    });
  }

  private verifyTokens(): Observable<boolean> {
    if (!this.areTokensValid()) {
      return this.getEcTokenAndSetCookie().pipe(
        map((tokens) => {
          return tokens != null;
        })
      );
    }
    return of(true);
  }

  private getTokens(): ECTokensDto {
    if (this.tokens) {
      return this.tokens;
    }

    const person_id = this.cookieService.get('__oe_pid');
    const ec_token = this.cookieService.get('__oe_ec');
    const ec_access_token = this.cookieService.get('__oe_ec_access');
    const tokens: ECTokensDto = {
      loginToken: ec_token,
      accessToken: ec_access_token,
      personId: person_id,
    };
    return tokens;
  }

  private isPersonIdValid(personId: String): Boolean {
    const person = this.oeAppPersonService.getPersonData();
    return person && personId === String(person.id);
  }

  private areTokensValid(): Boolean {
    const tokens = this.getTokens();
    return tokens.loginToken && tokens.accessToken && this.isPersonIdValid(tokens.personId);
  }

  private getEcTokenAndSetCookie(): Observable<any> {
    const data = { tokenType: 'JWT', personId: this.oeAppPersonService.id };
    return this.http
      .post<ECTokensDto>(`${this.oeApp.thirdPartyServiceHost}/v1/ectokens`, data)
      .pipe(
        map((result) => {
          const person = this.oeAppPersonService.getPersonData();

          const tokens = {
            login_token: result.loginToken,
            access_token: result.accessToken,
            person: person,
            personId: person.id.toString(),
          };

          this.cookieService.put('__oe_pid', tokens.personId);
          this.cookieService.put('__oe_ec', tokens.login_token);
          this.cookieService.put('__oe_ec_access', tokens.access_token);

          return tokens;
        })
      );
  }

  private reportEcVideoProgress(retries: number = 0): Observable<any> {
    const { mediaData, id } = this.video;
    if (retries > this.maxRetries) {
      console.error('Error retrieving activity progress. Saving course progress failed.');
      return throwError('Error retrieving activity progress.');
    }

    if (!this.areTokensValid()) {
      return this.getEcTokenAndSetCookie().pipe(mergeMap((result) => this.reportEcVideoProgress(++retries)));
    }

    const tokens = this.getTokens();
    const reportCardUrl = this.oeApp.getData('common').englishCentralReportCardUrl;
    const url = reportCardUrl + '/rest/report/dialog/' + mediaData + '/progress?useNewContentWords=true';
    const headers = new HttpHeaders({
      accept: 'application/vnd.englishcentral-v1+json,application/json;q=0.9,*/*;q=0.8',
      authorization: 'JWT ' + tokens.accessToken,
    });

    return this.http
      .get(url, {
        responseType: 'json',
        headers,
      })
      .pipe(
        mergeMap((result) => this.saveProgress(result)),
        catchError((result: HttpErrorResponse) => {
          return this.getEcTokenAndSetCookie().pipe(map(() => this.reportEcVideoProgress(++retries)));
        })
      );
  }

  private saveVideoProgress(isComplete = false): Observable<any> {
    if (!this.video || !this.video.id) {
      return;
    }

    return this.immersionCourseService.savePersonCourse(
      Math.ceil((new Date().getTime() - this.startTime) / 1000),
      isComplete,
      this.video.id,
      CourseContentType.VIDEOS
    );
  }

  private saveProgress(EcData: any): Observable<any> {
    const { id } = this.video;

    let complete = false;
    let isWatchComplete = false;
    let isLearnComplete = false;
    let isSpeakComplete = false;

    if (EcData && EcData.activities) {
      EcData.activities.forEach((activity) => {
        switch (activity.activityType) {
          case 'watchActivity':
            isWatchComplete = activity.completed;
            break;
          case 'learnActivity':
            isLearnComplete = activity.completed;
            break;
          case 'speakActivity':
            isSpeakComplete = activity.completed;
            break;
          default:
            console.error('Error: activity not recognized: ' + activity.activityType);
        }
      });
      complete = isWatchComplete && isLearnComplete && isSpeakComplete;

      return this.saveVideoProgress(complete);
    }
    return of(null);
  }

  private playVideo() {
    if (!this.player) {
      this.modalRef.close();
      return;
    }
    const that = this;
    this.verifyTokens().subscribe((areValid) => {
      if (!areValid) {
        this.modalRef.close();
      }
      const tokens = this.getTokens();
      that.player.innerHTML = '';
      that.startTime = new Date().getTime();

      window.ECSDK.loadWidget('player', {
        partnerKey: that.commonConst.ENGLISH_CENTRAL_APP_ID,
        partnerSdkToken: tokens.loginToken,
        dialogId: that.video.mediaData,
        lang: that.videoLanguage,
        container: this.player.id,
      });
    });
  }

  private playVideoMobile() {
    if (this.oeApp.isAndroidDevice() && this.oeApp.isOpenEnglishDevice() && window.oeAndroid) {
      window.oeAndroid.playEC(this.video.mediaData, 'MobileEC.saveProgress');
      window.MobileEC.courseId = this.video.id;
    } else if (this.oeApp.isIosDevice()) {
      window.MobileEC.courseId = this.video.id;
      location.href = 'playec://lp2?dialogid=' + this.video.mediaData + '&callback=' + 'MobileEC.saveProgress';
    }
  }
}



// WEBPACK FOOTER //
// ./ts/app/shared/ec-modal/ec-video.service.ts